import type { State } from './api'

/**
 * Formatting for the Pulse screen, kept out of the rendering.
 *
 * Both functions here are judgements about what to SHOW, not markup, and both
 * are easy to get subtly wrong — the same reasoning `suggestions.ts` gives for
 * keeping `basisOf` and `addsSomething` out of the Inbox component.
 */

/**
 * A readable name for a page: its own title, then a name built from the slug.
 * The same order the sidebar uses, minus the `label` a navigation entry can
 * override with — this screen has no navigation tree to read one from.
 */
export function titleOf(state: State, path: string): string {
  const id = path.replace(/^pages\//, '').replace(/\.md$/, '')
  const page = state.pages.find((p) => p.path === path)
  if (page?.title) return page.title
  if (id === 'index') return 'Home'
  const last = id.split('/').pop() ?? id
  return last.replace(/-/g, ' ').replace(/^./, (c) => c.toUpperCase())
}

/**
 * How long ago, in words. `null` in, `null` out: an unknown age is said
 * nowhere, never guessed at as "0 days ago".
 */
export function ago(iso: string | null): string | null {
  if (!iso) return null
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days} days ago`
  const months = Math.round(days / 30)
  if (months < 12) return months === 1 ? 'a month ago' : `${months} months ago`
  const years = Math.round(days / 365)
  return years === 1 ? 'a year ago' : `${years} years ago`
}
